import { ReactElement } from "react";

import EnvelopeIcon from "@src/assets/icons/envelope.svg";
import UnassignIcon from "@src/assets/icons/unassign.svg";
import { AuctionTypes } from "@src/constants";
import {
  ProjectInformationRenderer,
  TextSubtextRenderer,
} from "@src/custom_renderer";
import { FrameworkComponents } from "@src/types/aggrid_types";
import {
  LinkRendererValue,
  ProjectInformationRendererValue,
  TextSubtextRendererValue,
} from "@src/types/custom_renderer_types";
import {
  DigixMilestone,
  DigixPhase,
  DigixProject,
} from "@src/types/project_types";
import { ResourcePlanOverviewTableRowData } from "@src/types/resource_plan_types";
import { cellWrapText, columnTypes } from "@src/utils/aggrid_utils";
import {
  ColDef,
  GetDetailRowDataParams,
  GetRowIdParams,
  GridOptions,
  GridReadyEvent,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-enterprise";
import { CustomCellRendererProps } from "ag-grid-react";
import { Badge } from "antd";
import dayjs from "dayjs";

import { LinkRenderer, StaffingProgressBar } from "../custom_renderer";

/* GRID OPTIONS */

/**
 * Defines the grid options for the master table
 *
 * @returns the grid options of the master table
 */
export function getResourcePlanOverviewGridOptions(): GridOptions {
  return {
    getRowId: (params: GetRowIdParams<ResourcePlanOverviewTableRowData>) => {
      return params.data.resourcePlanId.toString();
    },
    masterDetail: true,
    detailCellRendererParams: getDetailCellRendererParams(),
    suppressContextMenu: true,
    detailRowAutoHeight: true,
    rowHeight: 50,
    domLayout: "autoHeight",
    headerHeight: 0,
    defaultColDef: {
      ...columnTypes.default,
    },
    columnDefs: getMasterColumnDefs(),
    components: masterComponents(),
  };
}

/**
 * Defines the grid options and row data for the details table
 *
 * @returns the detail cell renderer params that are used in the master grid options to render the details table
 */
function getDetailCellRendererParams(): {
  detailGridOptions: GridOptions;
  getDetailRowData: (params: GetDetailRowDataParams) => void;
} {
  return {
    detailGridOptions: {
      rowHeight: 50,
      domLayout: "autoHeight",
      suppressContextMenu: true,
      defaultColDef: {
        ...columnTypes.default,
        resizable: false,
        sortable: false,
        unSortIcon: false,
        minWidth: 80,
      },
      columnDefs: getDetailColumnDefs(),
      components: detailsComponents(),
      onGridReady: (event: GridReadyEvent) => {
        if (event.api.isRowDataEmpty()) {
          event.api.setGridOption("headerHeight", 0);
        }
      },
    },
    getDetailRowData: (
      params: GetDetailRowDataParams<
        ResourcePlanOverviewTableRowData,
        Partial<DigixProject>
      >
    ) => {
      params.successCallback(params.data.projects);
    },
  };
}

/* COMPONENTS */

/**
 * Framework components for the master table
 *
 * @returns the components that are rendered in the master table
 */
function masterComponents(): FrameworkComponents {
  return {
    renderStaffingProgressBar: (props: CustomCellRendererProps) => {
      return <StaffingProgressBar props={props} />;
    },
  };
}

/**
 * Framework components for the detailed table
 *
 * @returns the components that are rendered in the details table
 */
function detailsComponents(): FrameworkComponents {
  return {
    renderProjectInformation: (props: CustomCellRendererProps) => {
      return <ProjectInformationRenderer props={props} />;
    },
    renderTextSubtext: (props: CustomCellRendererProps) => {
      return <TextSubtextRenderer props={props} />;
    },
  };
}

/* COLUMN DEFS */

/**
 * Defines the columns of the master table
 *
 * @returns array of column definitions
 */
function getMasterColumnDefs(): ColDef[] {
  return [
    {
      field: "resourcePlanName",
      tooltipValueGetter: (params: ITooltipParams) => `${params.value.text}`,
      valueGetter: (params: ValueGetterParams): LinkRendererValue => ({
        text: `${params.data.resourcePlanName} (${params.data.projects?.length})`,
        path: `/projects/${params.data.resourcePlanId}`,
      }),
      minWidth: 230,
      flex: 2,
      cellClass: "resource-plan-name",
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams: {
        innerRenderer: (props: CustomCellRendererProps) => (
          <LinkRenderer props={props} />
        ),
      },
    },
    {
      field: "totalActionRequired",
      minWidth: 50,
      flex: 1,
      cellRenderer: (props: CustomCellRendererProps) => {
        const isDisabled = !props.value;
        return (
          <Badge count={props.value} size="small">
            <EnvelopeIcon
              style={{ opacity: isDisabled ? 0.3 : 1, width: 24, height: 24 }}
            />
          </Badge>
        );
      },
    },
    {
      minWidth: 160,
      flex: 3,
      cellRenderer: "renderStaffingProgressBar",
      valueGetter: (params: ValueGetterParams) => {
        if (!params.data.totalRequested || !params.data.totalStaffed)
          return [0];
        const calculatedPercent =
          (100 / params.data.totalRequested) * params.data.totalStaffed;
        return [Math.round(calculatedPercent)];
      },
    },
    {
      field: "totalStaffed",
      minWidth: 90,
      flex: 1,
      cellClass: "small-font-size",
      cellRenderer: (props: CustomCellRendererProps) =>
        renderRequestTotals(props.value, "staffed"),
    },
    {
      field: "totalRequested",
      minWidth: 110,
      flex: 1,
      cellClass: "small-font-size",
      cellRenderer: (props: CustomCellRendererProps) =>
        renderRequestTotals(props.value, "requested"),
    },
    {
      field: "totalOpen",
      minWidth: 80,
      flex: 1,
      cellClass: "small-font-size",
      cellRenderer: (props: CustomCellRendererProps) =>
        renderRequestTotals(props.value, "open"),
    },
  ];
}

/**
 * Defines the columns for the details table
 *
 * @returns array of column definitions
 */
function getDetailColumnDefs(): ColDef[] {
  return [
    {
      field: "name",
      headerName: "Project",
      headerTooltip: "Project",
      autoHeight: true,
      minWidth: 290,
      flex: 3,
      cellStyle: cellWrapText,
      cellRenderer: "renderProjectInformation",
      valueGetter: (
        params: ValueGetterParams
      ): ProjectInformationRendererValue => {
        return {
          projectName: params.data.name,
          projectPhase:
            params.data.phases.find((phase: DigixPhase) => phase.isCurrent)
              ?.name || "",
          technology: params.data.technology,
          projectLeadEmployeeName: params.data.projectManager,
          isHydrogen: params.data.isHydrogen,
        };
      },
      comparator(
        valueA: ProjectInformationRendererValue,
        valueB: ProjectInformationRendererValue
      ) {
        return valueA.projectName > valueB.projectName ? 1 : -1;
      },
    },
    {
      field: "country",
      headerName: "Region / Country",
      headerTooltip: "Region / Country",
      autoHeight: true,
      minWidth: 100,
      flex: 2,
      cellStyle: cellWrapText,
      cellRenderer: "renderTextSubtext",
      valueGetter: (params: ValueGetterParams): TextSubtextRendererValue => {
        return {
          text: params.data.region,
          subtext: params.data.country,
        };
      },
      comparator(
        valueA: TextSubtextRendererValue,
        valueB: TextSubtextRendererValue
      ) {
        return valueA.text > valueB.text ? 1 : -1;
      },
    },
    {
      field: "mtpCategory",
      headerName: "MTP Category",
      headerTooltip: "MTP Category",
      flex: 1,
      cellStyle: cellWrapText,
    },
    {
      field: "grossCapacity",
      headerName: "Gross Cap.",
      headerTooltip: "Gross Cap.",
      valueFormatter: (params: ValueFormatterParams) => `${params.value}MW`,
      flex: 1,
    },
    {
      field: "auctionDate",
      headerName: "Auction Date",
      headerTooltip: "Auction Date",
      autoHeight: true,
      flex: 1,
      cellStyle: cellWrapText,
      cellRenderer: "renderTextSubtext",
      valueGetter: (params: ValueGetterParams): TextSubtextRendererValue => {
        const auctionPhase: DigixPhase = params.data.phases[1];
        const auctionDate: Date = getAuctionMilestoneDate(
          auctionPhase,
          params.data.auctionType
        );

        return {
          text: auctionDate ? dayjs(auctionDate).format("MM.YYYY") : "",
          subtext: params.data.auctionType,
        };
      },
      comparator(
        valueA: TextSubtextRendererValue,
        valueB: TextSubtextRendererValue
      ) {
        return valueA.text > valueB.text ? 1 : -1;
      },
    },
    {
      field: "FID",
      headerName: "FID",
      headerTooltip: "FID",
      flex: 1,
      valueGetter: (params: ValueGetterParams) => {
        const fidPhase: DigixPhase = params.data.phases[2];
        const fidMilestone: DigixMilestone = fidPhase.milestones[0];
        return dayjs(fidMilestone.date).year();
      },
    },
    {
      field: "COD",
      headerName: "COD",
      headerTooltip: "COD",
      flex: 1,
      valueGetter: (params: ValueGetterParams) => {
        const codPhase: DigixPhase = params.data.phases[3];
        const codMilestone: DigixMilestone = codPhase.milestones[0];
        return dayjs(codMilestone.date).year();
      },
    },
    {
      field: "detachProject",
      headerName: "",
      resizable: false,
      sortable: false,
      minWidth: 66,
      maxWidth: 66,
      cellRenderer: () => <UnassignIcon />,
    },
  ];
}

/* HELPER */

/**
 * Renders the DOM for the request totals columns
 *
 * @param total number of totals
 * @param text text of what kind of totals is shown
 * @returns the ReactElement for the totals
 */
function renderRequestTotals(total: string, text: string): ReactElement {
  return (
    <span>
      <label>{total}</label> {text}
    </span>
  );
}

/**
 * Searches for the right milestone in order to get the auction date related to the auction type
 
 * @param preBidPhase the pre bid phase of the project
 * @param auctionType the auction type of the project
 * @returns the date of the auction related milestone
 */
function getAuctionMilestoneDate(
  preBidPhase: DigixPhase,
  auctionType: AuctionTypes
): Date | undefined {
  if (!auctionType || auctionType === AuctionTypes.NOT_APPLICABLE) return;

  const milestoneName =
    auctionType === AuctionTypes.OFFTAKE
      ? "offtakeAuctionDate"
      : "leaseAuctionDate";

  const milestone: DigixMilestone = preBidPhase.milestones.find(
    (milestone) => milestone.name === milestoneName
  );

  return milestone?.date;
}
